import React, { useState, useEffect, useCallback } from 'react';
import Select from 'react-select';
import { FaUser, FaEnvelope, FaPhone, FaRegCommentDots, FaStore, FaHandHoldingUsd, FaMapMarkerAlt, FaCity } from 'react-icons/fa';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { motion, AnimatePresence } from 'framer-motion';

import { getApiUrl } from '../../utils/api';
import './InquiryForm.css';

// ====================================================================
// 1. CONSTANTS & CONFIGURATION (Defined outside the component)
// ====================================================================

const INITIAL_FORM_STATE = {
  inquiry_type: '',
  name: '',
  email: '',
  contact: '',
  state_id: '',
  city_id: '',
  message: '',
};

// Data for the advice cards on the left
const adviceCardsData = [
  {
    icon: <FaStore />,
    title: 'Why Expand Your Brand',
    description: 'Take your business to the next level with our proven expansion strategies.',
    points: [
      'Expand into high-potential markets across India.',
      'Leverage Franmax India’s strong franchise network.',
      'Reduce risk with expert guidance and market insights.',
    ],
  },
  {
    icon: <FaHandHoldingUsd />,
    title: 'Why Invest in a Franchise',
    description: 'Franchising offers a low-risk and high-return pathway to business ownership.',
    points: [
      'Access trusted brands with proven business models.',
      'Enjoy structured training and operational support.',
      'Faster ROI compared to starting an independent business.',
    ],
  },
  {
    icon: <FaUser />,
    title: 'Why Choose Franmax India',
    description: 'We are India’s most trusted franchise growth partner with decades of expertise.',
    points: [
      '500+ brands across multiple industries to choose from.',
      'Tailored solutions for investors and business owners.',
      'Comprehensive support from start to scale-up.',
    ],
  },
];

// Styles for React-Select, using CSS variables for consistency
const selectStyles = {
  control: (provided, state) => ({
    ...provided,
    borderRadius: '18px',
    border: state.isFocused ? '1px solid var(--primary-blue)' : '1px solid var(--glass-border)',
    boxShadow: state.isFocused ? '0 0 18px var(--glow-color)' : 'none',
    minHeight: '48px',
    fontSize: '15px',
    background: 'rgba(0, 0, 0, 0.2)',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    '&:hover': {
      borderColor: 'var(--primary-blue)',
    },
  }),
  singleValue: (provided) => ({ ...provided, color: 'var(--text-light)', fontSize: '15px' }),
  placeholder: (provided) => ({ ...provided, color: 'var(--text-muted)', fontSize: '15px' }),
  menu: (provided) => ({ ...provided, borderRadius: '18px', overflow: 'hidden', fontSize: '15px', background: 'var(--bg-dark-end)', border: '1px solid var(--glass-border)' }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isFocused ? 'var(--primary-blue)' : 'transparent',
    color: 'var(--text-light)',
    cursor: 'pointer',
  }),
  input: (provided) => ({ ...provided, color: 'var(--text-light)' }),
};


// ====================================================================
// 2. MAIN COMPONENT
// ====================================================================

const InquiryForm = ({ defaultInquiryType = '' }) => {
  const [formData, setFormData] = useState({ ...INITIAL_FORM_STATE, inquiry_type: defaultInquiryType });
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState({ states: false, cities: false });
  const [status, setStatus] = useState('idle'); // 'idle' | 'submitting' | 'success'

  const inquiryOptions = [
      { value: "expand", label: "Expand Your Brand" },
      { value: "franchise", label: "Franchise Inquiry" },
  ];
  
  const handleSelectChange = useCallback((name, option) => {
    setFormData(prev => {
      const newState = { ...prev, [name]: option?.value || '' };
      if (name === 'state_id') {
        newState.city_id = ''; // Reset city when state changes
      }
      return newState;
    });
  }, []);

  // ✅ --- THIS IS THE ADDED PART --- ✅
  // This hook listens for the custom event from the Banner component.
  useEffect(() => {
    const handleSetInquiryType = (event) => {
      const { type } = event.detail;
      if (type) {
        // Find the full {value, label} option object that matches the type
        const selectedOption = inquiryOptions.find(opt => opt.value === type);
        if (selectedOption) {
          // Use your existing handler to update the form's state correctly
          handleSelectChange('inquiry_type', selectedOption);
        }
      }
    };

    // Add the listener when the component mounts
    window.addEventListener('setInquiryType', handleSetInquiryType);

    // Clean up the listener when the component unmounts to prevent memory leaks
    return () => {
      window.removeEventListener('setInquiryType', handleSetInquiryType);
    };
  }, [inquiryOptions, handleSelectChange]); // Dependencies ensure the function uses the latest state and handlers

  // Fetch Indian states on component mount
  useEffect(() => {
    const controller = new AbortController();
    const fetchStates = async () => {
      setLoading(prev => ({ ...prev, states: true }));
      try {
        const res = await fetch(getApiUrl('get-indian-states.php'), { signal: controller.signal });
        const data = await res.json();
        if (Array.isArray(data)) {
          setStates(data.map(s => ({ label: s.name, value: s.id })));
        }
      } catch (error) {
        if (error.name !== 'AbortError') toast.error('Failed to load states');
      } finally {
        setLoading(prev => ({ ...prev, states: false }));
      }
    };
    fetchStates();
    return () => controller.abort(); // Cleanup function
  }, []);

  // Fetch cities when state changes
  useEffect(() => {
    if (!formData.state_id) {
      setCities([]);
      return;
    }
    const controller = new AbortController();
    const fetchCities = async () => {
      setLoading(prev => ({ ...prev, cities: true }));
      try {
        const res = await fetch(getApiUrl(`get-cities.php?state_id=${formData.state_id}`), { signal: controller.signal });
        const data = await res.json();
        if (Array.isArray(data)) {
          setCities(data.map(c => ({ label: c.name, value: c.id })));
        }
      } catch (error) {
        if (error.name !== 'AbortError') toast.error('Failed to load cities');
      } finally {
        setLoading(prev => ({ ...prev, cities: false }));
      }
    };
    fetchCities();
    return () => controller.abort(); // Cleanup function
  }, [formData.state_id]);

  // Generic handler for all input/textarea changes
  const handleInputChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  }, []);

  // Form validation logic
  const validateForm = () => {
    const { inquiry_type, name, email, contact, state_id, city_id, message } = formData;
    if (!inquiry_type) return 'Inquiry type is required';
    if (!name.trim() || !/^[A-Za-z\s]+$/.test(name.trim())) return 'Name must contain only letters and spaces';
    if (!email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) return 'Invalid email address';
    if (!/^[6-9]\d{9}$/.test(contact)) return 'Invalid 10-digit mobile number';
    if (!state_id) return 'State is required';
    if (!city_id) return 'City is required';
    if (!message.trim()) return 'Message is required';
    return null; // No errors
  };

  // Form submission handler
  const handleSubmit = async (e) => {
    e.preventDefault();
    const error = validateForm();
    if (error) {
      toast.error(error);
      return;
    }

    setStatus('submitting');
    try {
      const res = await fetch(getApiUrl('submit-footer-inquiry.php'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });
      const data = await res.json();
      if (data.success) {
        toast.success('✅ Inquiry submitted successfully!');
        setStatus('success');
        setTimeout(() => {
          setFormData({ ...INITIAL_FORM_STATE, inquiry_type: defaultInquiryType });
          setStatus('idle');
        }, 5000);
      } else {
        toast.error(data.error || '❌ Failed to submit inquiry');
        setStatus('idle');
      }
    } catch {
      toast.error('❌ An unexpected error occurred!');
      setStatus('idle');
    }
  };
  
  return (
    <div className="inquiry-main">
      <ToastContainer position="top-right" autoClose={3000} theme="dark" />
      <div className="inquiry-row">
        
        {/* LEFT COLUMN */}
        <div className="inquiry-left">
          <h3 className="inquiry-title">Why Franmax India?</h3>
          {adviceCardsData.map((card, index) => (
            <motion.div
              className="advice-card"
              key={index}
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: index * 0.2 }}
            >
              <div className="advice-icon">{card.icon}</div>
              <div className="advice-info">
                <h4>{card.title}</h4>
                <p>{card.description}</p>
                <ul className="advice-list">
                  {card.points.map((point, i) => <li key={i}>{point}</li>)}
                </ul>
              </div>
            </motion.div>
          ))}
        </div>

        {/* RIGHT COLUMN (FORM) */}
        <div className="inquiry-right" id='inquiry-form'>
          <AnimatePresence mode="wait">
            {status === 'success' ? (
              <motion.div
                key="thank-you"
                initial={{ opacity: 0, y: 40 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -40 }}
                transition={{ duration: 0.6, ease: "easeOut" }}
                className="thank-you-box"
              >
                <h2>🎉 Thank You!</h2>
                <p>Your inquiry has been received. Our team will contact you shortly.</p>
              </motion.div>
            ) : (
              <motion.div
                key="form"
                initial={{ opacity: 0, scale: 0.98 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.98 }}
                transition={{ duration: 0.5 }}
              >
                <h3 className="footer-form-title">Request a Call Back</h3>
                <p className="footer-form-subtitle">Share your goals with us</p>

                <form className="inquiry-form" onSubmit={handleSubmit} noValidate>
                  <Select
                    options={inquiryOptions}
                    value={inquiryOptions.find(opt => opt.value === formData.inquiry_type) || null}
                    onChange={(option) => handleSelectChange('inquiry_type', option)}
                    placeholder="Select Your Purpose *"
                    styles={selectStyles}
                    className="full-width"
                    required
                  />

                  <div className="footer-form-row">
                      <FormField
                        name="name"
                        type="text"
                        placeholder="Full Name *"
                        value={formData.name}
                        onChange={handleInputChange}
                        icon={<FaUser />}
                      />
                  </div>

                  <div className="footer-form-row">
                      <FormField
                        name="email"
                        type="email"
                        placeholder="Email *"
                        value={formData.email}
                        onChange={handleInputChange}
                        icon={<FaEnvelope />}
                        width="half-width"
                      />
                      <FormField
                        name="contact"
                        type="tel"
                        placeholder="Mobile Number *"
                        value={formData.contact}
                        onChange={handleInputChange}
                        icon={<FaPhone />}
                        width="half-width"
                      />
                  </div>

                  <div className="footer-form-row">
                      <SelectField
                        name="state_id"
                        value={states.find(s => s.value === formData.state_id) || null}
                        onChange={(option) => handleSelectChange('state_id', option)}
                        options={states}
                        placeholder={loading.states ? 'Loading states...' : 'Select State *'}
                        isLoading={loading.states}
                        width="half-width"
                      />
                      <SelectField
                        name="city_id"
                        value={cities.find(c => c.value === formData.city_id) || null}
                        onChange={(option) => handleSelectChange('city_id', option)}
                        options={cities}
                        placeholder={loading.cities ? 'Loading cities...' : 'Select City *'}
                        isLoading={loading.cities}
                        isDisabled={!formData.state_id || loading.cities}
                        width="half-width"
                      />
                  </div>
                  
                  <div className="footer-form-row">
                    <FormField
                      name="message"
                      type="textarea"
                      placeholder="Type your requirement *"
                      value={formData.message}
                      onChange={handleInputChange}
                      icon={<FaRegCommentDots />}
                    />
                  </div>
                  
                  <div className="footer-form-row">
                    <button type="submit" className="submit-btn" disabled={status === 'submitting'}>
                      {status === 'submitting' ? 'Submitting...' : 'Submit'}
                    </button>
                  </div>
                </form>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};


// ====================================================================
// 3. REUSABLE SUB-COMPONENTS
// ====================================================================

// Reusable component for standard input fields
const FormField = ({ name, type, placeholder, value, onChange, icon, width = 'full-width' }) => (
  <div className={`footer-form-field ${width}`}>
    {icon && React.cloneElement(icon, { className: 'footer-form-icon' })}
    {type === 'textarea' ? (
      <textarea
        name={name}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        required
      />
    ) : (
      <input
        type={type}
        name={name}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        required
      />
    )}
  </div>
);

// Reusable component for React-Select fields
const SelectField = ({ name, value, onChange, options, placeholder, isLoading, isDisabled, width = 'full-width' }) => (
  <div className={`footer-form-field ${width}`}>
    <Select
      name={name}
      value={value}
      onChange={onChange}
      options={options}
      placeholder={placeholder}
      isLoading={isLoading}
      isDisabled={isDisabled}
      isSearchable
      styles={selectStyles}
      required
    />
  </div>
);


export default InquiryForm;